import { isFreeSpinsBundleItem, isMetaGameBundleItem } from '../rewards/helpers';
import type { BundleItem, BundleItemOfType } from '../rewards/types';

/**
 * The providers whose grants are mini-game rounds, not casino free spins: `FRBWidget` draws them as
 * their art alone and the free-spins store keeps them out of its list. Compared case-insensitively,
 * since an admin form saves `minigames`.
 */
const MINIGAME_PROVIDERS = ['barouletta gaming', 'minigames'];

/**
 * How the prize row draws one bundle item, as the product draws it elsewhere:
 * - `image`: a mini-game round, drawn as its art only (no "FREE SPINS" label).
 * - `freeSpins`: casino free spins, drawn by `FRBWidget`.
 * - `metaToken`: a metagame token, drawn by `MetaGameWidget`: its icon and amount.
 * - `widget`: anything else, drawn by `BundleItemsRenderer`.
 */
export type BattlePrizeItem =
  | { kind: 'image'; key: string; imageUrl: string }
  | { kind: 'freeSpins'; key: string; item: BundleItemOfType<'freeSpins'> }
  | { kind: 'metaToken'; key: string; item: BundleItemOfType<'metagameToken'> }
  | { kind: 'widget'; key: string; item: BundleItem };

/** A mini-game round: its own bundle type, or a free-spins grant from a mini-game provider. */
export function isMinigameRoundBundleItem(item: BundleItem): boolean {
  if (item.bundleType === 'minigameRound') {
    return true;
  }

  return isFreeSpinsBundleItem(item) && MINIGAME_PROVIDERS.includes(String(item.bundlePayload.provider).toLowerCase());
}

/** Casino free spins, which `FRBWidget` draws with their count and bet. */
export function isCasinoFreeSpinsBundleItem(item: BundleItem): item is BundleItemOfType<'freeSpins'> {
  return item.bundleType === 'freeSpins' && !isMinigameRoundBundleItem(item);
}

const toPrizeItem = (item: BundleItem, index: number): BattlePrizeItem | null => {
  const key = item.id || `${item.bundleType}-${index}`;

  if (isMinigameRoundBundleItem(item) && isFreeSpinsBundleItem(item)) {
    const imageUrl = item.bundlePayload.gameImageUrl || item.bundlePayload.imageUrl;

    // No art, nothing to draw: a label would be the "FREE SPINS" text this row must not show.
    return imageUrl ? { kind: 'image', key, imageUrl } : null;
  }

  if (isCasinoFreeSpinsBundleItem(item)) {
    return { kind: 'freeSpins', key, item };
  }

  if (isMetaGameBundleItem(item)) {
    return { kind: 'metaToken', key, item };
  }

  return { kind: 'widget', key, item };
};

/** The prize's bundle items, in their order, as the row draws them. */
export function selectBattlePrizeItems(bundleItems: BundleItem[] | undefined): BattlePrizeItem[] {
  return (bundleItems ?? []).map(toPrizeItem).filter((prize): prize is BattlePrizeItem => prize !== null);
}
